import { Dispatch, SetStateAction, useRef, ChangeEvent, useMemo } from "react"
import * as Yup from 'yup';

export function usePlainStateFormUtils<S extends { prevData: any, stage: any, errors: any, touched: any }>(
  states: S, 
  setStates: Dispatch<SetStateAction<S>>, validateSchema: Yup.AnySchema, 
  setValueProp?: (path: keyof S['stage'], hookSetVal: (newValue: any) => void) => (newValue: any) => void
) {
  const {validate, defaultSetValue} = useMemo(() => {
    const validate = (x: S['stage']) => {
      try {
        validateSchema.validateSync(x, {abortEarly:false})
      } catch (e: any) {
        if (e.name === 'ValidationError') {
          const errors = {} as Record<string, string>
          for (let err of e.inner) {
            errors[err.path] = err.message
          }
          return errors
        }
      }
    }
    const defaultSetValue = (path: keyof S['stage']) => (newValue: any) => {
      setStates(v => {
        (v.stage as any)[path] = newValue
        if (v.prevData[path] !== newValue) {
          v.touched[path] = true
        } else {
          delete v.touched[path]
        }
        v.errors = validate(v.stage) ?? {}
        return {...v}
      })
    }
    return ({validate, defaultSetValue})
  }, [setStates, validateSchema])
  const timerRef = useRef({} as any)
  const fieldProps = (path: keyof S['stage'], transform?: (x:any)=>any) => ({
    value: states.stage[path],
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (timerRef.current[path]) {
        window.clearTimeout(timerRef.current[path])
        delete timerRef.current[path]
      }
      const value = transform ? transform(e.target.value) : e.target.value;
      const defaultSetValueFn = defaultSetValue(path)
      const setValue = setValueProp ? setValueProp(path, defaultSetValueFn) : defaultSetValueFn
      setValue(value)
      if (typeof value === 'string' && value.trim() !== value) {
        timerRef.current[path] = setTimeout(() => setValue(value.trim()), 1500)
      }
    },
    error: Boolean(states.touched[path] && states.errors[path]),
    helperText: states.touched[path] ? states.errors[path] : undefined,
  })
  return {validate, fieldProps}
}
